import React, { useEffect, useState } from "react";
import { db } from "./firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./ViewMyScores.css";

const ViewMyScores = ({ user }) => {
  const navigate = useNavigate();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Keep track of which quizCode is expanded
  const [expandedQuizCode, setExpandedQuizCode] = useState(null);
  // Track hover state for each quiz card
  const [hoveredCard, setHoveredCard] = useState(null);
  // Track archived quizzes
  const [archivedQuizzes, setArchivedQuizzes] = useState([]);
  // Toggle to show archived quizzes
  const [showArchived, setShowArchived] = useState(false);
  // For image modal/lightbox
  const [modalImage, setModalImage] = useState(null);
  // Track which questions are expanded in the detailed view
  const [expandedQuestions, setExpandedQuestions] = useState({});

  // Fetch only the current student's submissions
  useEffect(() => {
    const fetchMySubmissions = async () => {
      if (!user?.uid) return;

      setLoading(true);
      try {
        const subsRef = collection(db, "submissions");
        const q = query(subsRef, where("userId", "==", user.uid));
        const snapshot = await getDocs(q);

        const responses = [];

        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          if (!data.questionId) continue; // skip incomplete data

          // Grab question data
          const questionId = data.questionId.includes("/")
            ? data.questionId.split("/").pop() // handle "questions/abc123" style
            : data.questionId;

          const questionDoc = await getDoc(doc(db, "questions", questionId));
          if (!questionDoc.exists()) continue;

          const questionData = questionDoc.data();

          responses.push({
            id: docSnap.id,
            quizCode: data.quizCode || "NoCode",
            questionText: questionData.questionText || "Unknown Question",
            questionImageURL: questionData.questionImageURL || null,
            userAnswer: data.userAnswer,
            markSchemeAnswer: questionData.markSchemeAnswer || "N/A",
            feedback: data.feedback || "Pending",
            maxMark: questionData.maxMark || 0,
            markAwarded: data.markAwarded ?? "Pending",
            submittedAt: data.submittedAt?.toDate() || new Date(),
          });
        }

        setSubmissions(responses);
      } catch (error) {
        console.error("Error fetching student submissions:", error);
        alert("Failed to load your quiz scores. Please try again.");
      }
      setLoading(false);
    };

    fetchMySubmissions();
  }, [user]);

  // Group by quizCode
  const groupedByQuiz = submissions.reduce((acc, sub) => {
    const { quizCode } = sub;
    if (!acc[quizCode]) {
      acc[quizCode] = [];
    }
    acc[quizCode].push(sub);
    return acc;
  }, {});

  // Get quiz codes with their latest submission date for sorting
  const quizCodesWithDates = Object.keys(groupedByQuiz).map(quizCode => {
    const latestDate = groupedByQuiz[quizCode].reduce((latest, sub) => {
      return sub.submittedAt > latest ? sub.submittedAt : latest;
    }, new Date(0));
    
    return { quizCode, latestDate };
  });
  
  // Sort by latest submission date (newest first)
  quizCodesWithDates.sort((a, b) => b.latestDate - a.latestDate);
  
  // Get the sorted quiz codes
  const quizCodes = quizCodesWithDates.map(item => item.quizCode);

  // Function to expand/collapse a given quizCode tile
  const toggleExpand = (quizCode) => {
    // Reset expanded questions when changing quiz
    if (quizCode !== expandedQuizCode) {
      setExpandedQuestions({});
    }
    setExpandedQuizCode((prev) => (prev === quizCode ? null : quizCode));
  };
  
  // Function to toggle question expansion
  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };
  
  // Load archived quizzes from localStorage on component mount
  useEffect(() => {
    if (user?.uid) {
      const storedArchived = localStorage.getItem(`archived_quizzes_${user.uid}`);
      if (storedArchived) {
        try {
          setArchivedQuizzes(JSON.parse(storedArchived));
        } catch (e) {
          console.error("Error parsing archived quizzes", e);
          // If there's an error, reset the archived quizzes
          localStorage.setItem(`archived_quizzes_${user.uid}`, JSON.stringify([]));
        }
      }
    }
  }, [user]);
  
  // Function to archive/unarchive a quiz
  const toggleArchiveQuiz = (quizCode) => {
    setArchivedQuizzes(prev => {
      let newArchived;
      if (prev.includes(quizCode)) {
        // Unarchive
        newArchived = prev.filter(qc => qc !== quizCode);
      } else {
        // Archive
        newArchived = [...prev, quizCode];
      }
      // Save to localStorage
      if (user?.uid) {
        localStorage.setItem(`archived_quizzes_${user.uid}`, JSON.stringify(newArchived));
      }
      return newArchived;
    });
  };

  // Helper to compute totals, date, etc. for each quiz
  const getQuizSummary = (quizSubmissions) => {
    const totalMarks = quizSubmissions.reduce(
      (acc, s) => acc + (parseFloat(s.markAwarded) || 0),
      0
    );
    const totalMax = quizSubmissions.reduce(
      (acc, s) => acc + (parseFloat(s.maxMark) || 0),
      0
    );
    const percentage =
      totalMax > 0 ? ((totalMarks / totalMax) * 100).toFixed(2) : "N/A";

    // e.g. latest submission date in the group
    const latestDate = quizSubmissions.reduce((latest, s) => {
      return s.submittedAt > latest ? s.submittedAt : latest;
    }, new Date(0)); // start from very old date

    const dateString = latestDate.toLocaleString();

    return { totalMarks, totalMax, percentage, dateString };
  };

  // Function to generate and download CSV feedback for a given quiz
  const downloadFeedback = (quizSubmissions, quizCode) => {
    const header = [
      "Question",
      "Your Answer",
      "Mark Scheme",
      "Feedback",
      "Mark Awarded",
      "Submitted At",
    ];
    const rows = quizSubmissions.map((sub) => [
      sub.questionText,
      sub.userAnswer,
      sub.markSchemeAnswer,
      sub.feedback,
      sub.markAwarded !== "Pending"
        ? `${sub.markAwarded}/${sub.maxMark}`
        : "Pending",
      sub.submittedAt.toLocaleString(),
    ]);

    const csvContent = [
      header.join(","),
      ...rows.map((row) =>
        row
          .map((cell) =>
            `"${cell.toString().replace(/"/g, '""')}"`
          )
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `feedback_${quizCode}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper function to format feedback text with line breaks
  const formatFeedback = (feedback) => {
    // Split by periods followed by a space and join with line breaks
    if (feedback === "Pending") return feedback;
    
    // Format the feedback with better spacing
    const formattedFeedback = feedback
      .replace(/\.\s+/g, '.\n\n') // Add double line breaks after periods
      .replace(/:\s+/g, ':\n') // Add line breaks after colons
      .replace(/Score:\s+/g, '\nScore: '); // Ensure Score is on its own line
      
    return formattedFeedback;
  };

  // QuizCard component for each quiz
  const QuizCard = ({ quizCode, submissions, isArchived, onArchiveToggle }) => {
    const { totalMarks, totalMax, percentage, dateString } = getQuizSummary(submissions);
    const isHovered = hoveredCard === quizCode;
    const isExpanded = expandedQuizCode === quizCode;

    // Calculate how old this quiz is
    const submissionDate = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - submissionDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Visual indicator if quiz is older than 90 days
    const isOld = diffDays > 90;

    const cardClasses = `quiz-card ${isHovered ? 'hovered' : ''} ${isArchived ? 'archived' : ''}`;
    const headerClasses = `quiz-header ${isArchived ? 'archived' : ''}`;

    return (
      <div 
        className={cardClasses}
        onMouseEnter={() => setHoveredCard(quizCode)}
        onMouseLeave={() => setHoveredCard(null)}
      >
        {/* Archive indicator */}
        {isArchived && (
          <div className="archive-indicator">
            Archived
          </div>
        )}
        
        {/* Age indicator for old quizzes */}
        {isOld && !isArchived && (
          <div className="age-indicator">
            {diffDays > 365 
              ? `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) !== 1 ? 's' : ''} old`
              : `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) !== 1 ? 's' : ''} old`}
          </div>
        )}
        
        <div className={headerClasses}>🏆</div>
        <div className="quiz-content">
          <h3 className="quiz-title">Quiz: {quizCode}</h3>
          <p className="quiz-info">
            <strong>Last Attempt:</strong> {dateString}
          </p>
          <div className={`score-highlight ${getScoreClass(parseFloat(percentage))}`}>
            {totalMarks} / {totalMax} ({percentage}%)
          </div>
          
          {/* Action buttons */}
          <div className="button-container">
            <button
              onClick={() => toggleExpand(quizCode)}
              className="action-button view"
            >
              {isExpanded ? "Hide Details" : "View Details"}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                downloadFeedback(submissions, quizCode);
              }}
              className="action-button download"
            >
              Download
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onArchiveToggle();
              }}
              className={`archive-button ${isArchived ? 'unarchive' : 'archive'}`}
            >
              {isArchived ? 'Unarchive Quiz' : 'Archive Quiz'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to get score class
  const getScoreClass = (percentage) => {
    if (percentage >= 80) return 'high';
    if (percentage >= 60) return 'medium';
    return 'low';
  };

  return (
    <div className="page-container">
      <div className="content-wrapper">
        {/* Header */}
        <header className="header">
          <div className="logo-container">
            <img src="/newlogo.png" alt="Logo" className="logo-image" />
            <h1 className="logo-text">AI Automarker</h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="back-button"
          >
            Back to Dashboard
          </button>
        </header>

        {/* Main Content */}
        <main className="main-content">
          <div className="welcome-card">
            <h2 className="welcome-title">My Quiz Scores</h2>
            <p>Track your performance across different quizzes and assessments.</p>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p className="loading-text">Loading your scores...</p>
            </div>
          ) : quizCodes.length > 0 ? (
            <>
              {/* Filter Controls */}
              <div className="filter-controls">
                <div>
                  <h3 className="filter-title">Quiz Results</h3>
                  <p className="filter-subtitle">
                    Showing {quizCodes.filter(qc => !archivedQuizzes.includes(qc) || showArchived).length} quizzes
                  </p>
                </div>
                <div>
                  <label className="archive-toggle-label">
                    <input 
                      type="checkbox" 
                      checked={showArchived} 
                      onChange={() => setShowArchived(!showArchived)}
                      className="archive-toggle"
                    />
                    Show Archived Quizzes
                  </label>
                </div>
              </div>
              
              {/* Quiz Cards Grid */}
              <div className="quiz-grid">
                {quizCodes
                  .filter(qc => !archivedQuizzes.includes(qc) || showArchived)
                  .map((qc) => (
                    <QuizCard 
                      key={qc} 
                      quizCode={qc} 
                      submissions={groupedByQuiz[qc]}
                      isArchived={archivedQuizzes.includes(qc)}
                      onArchiveToggle={() => toggleArchiveQuiz(qc)}
                    />
                  ))}
              </div>

              {/* Expanded Quiz Details - NEW EXPANDABLE LAYOUT */}
              {expandedQuizCode && (
                <div className="expanded-details">
                  <h3 className="details-title">
                    Detailed Results: {expandedQuizCode}
                  </h3>
                  
                  <div className="question-panels">
                    {groupedByQuiz[expandedQuizCode].map((sub, i) => {
                      // Use a consistent key for each question
                      const questionKey = `${expandedQuizCode}-${sub.id}`;
                      const isExpanded = expandedQuestions[questionKey] || false;
                      const panelClass = sub.markAwarded !== "Pending"
                        ? Number(sub.markAwarded) === Number(sub.maxMark)
                          ? "panel-full-marks"
                          : "panel-partial-marks"
                        : "panel-pending";
                        
                      return (
                        <div key={questionKey} className={`question-panel ${panelClass}`}>
                          {/* Question Header - Always visible */}
                          <div 
                            className="panel-header" 
                            onClick={() => toggleQuestion(questionKey)}
                          >
                            <div className="panel-header-content">
                              <div className="panel-question-preview">
                                <span className="question-number">Q{i+1}:</span> 
                                {sub.questionText.length > 80 
                                  ? `${sub.questionText.substring(0, 80)}...` 
                                  : sub.questionText}
                                {sub.questionImageURL && <span className="has-image-indicator"> [Has Image]</span>}
                              </div>
                              
                              <div className="panel-header-right">
                                {sub.markAwarded !== "Pending" ? (
                                  <div className={`mark-badge ${Number(sub.markAwarded) === Number(sub.maxMark) 
                                    ? 'full' 
                                    : Number(sub.markAwarded) > 0 ? 'partial' : 'zero'}`}>
                                    {sub.markAwarded}/{sub.maxMark}
                                  </div>
                                ) : (
                                  <div className="mark-badge pending">
                                    Pending
                                  </div>
                                )}
                                <div className="expand-icon">
                                  {isExpanded ? '▼' : '►'}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Question Details - Visible when expanded */}
                          {isExpanded && (
                            <div className="panel-details">
                              {/* Question Section */}
                              <div className="detail-section question-section">
                                <h4 className="section-title">Question</h4>
                                <div className="section-content">
                                  {sub.questionText !== "Unknown Question" && (
                                    <p className="question-text">{sub.questionText}</p>
                                  )}
                                  
                                  {sub.questionImageURL && (
                                    <div className="question-image-container">
                                      <img 
                                        src={sub.questionImageURL} 
                                        alt="Question" 
                                        className="question-image"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Your Answer Section */}
                              <div className="detail-section answer-section">
                                <h4 className="section-title">Your Answer</h4>
                                <div className="section-content">
                                  <div className="user-answer">
                                    {sub.userAnswer}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Two-column layout for Mark Scheme and Feedback */}
                              <div className="two-column-container">
                                {/* Mark Scheme Section */}
                                <div className="detail-section scheme-section">
                                  <h4 className="section-title">Mark Scheme</h4>
                                  <div className="section-content">
                                    {sub.feedback !== "Pending" && sub.markAwarded !== "Pending"
                                      ? (
                                        <div className="mark-scheme">
                                          {sub.markSchemeAnswer}
                                        </div>
                                      )
                                      : "Not yet available"}
                                  </div>
                                </div>
                                
                                {/* Feedback Section */}
                                <div className="detail-section feedback-section">
                                  <h4 className="section-title">Feedback</h4>
                                  <div className="section-content">
                                    {sub.feedback !== "Pending" ? (
                                      <div className="feedback-content">
                                        {formatFeedback(sub.feedback)}
                                      </div>
                                    ) : "Pending"}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Submission Info */}
                              <div className="submission-info">
                                <span className="submission-date-label">Submitted:</span> 
                                {sub.submittedAt.toLocaleDateString()} at {sub.submittedAt.toLocaleTimeString()}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <p className="empty-state-text">
                You haven't completed any quizzes yet.
              </p>
            </div>
          )}
        </main>
        
        <footer className="footer">
          <p>© {new Date().getFullYear()} AI Automarker. All rights reserved.</p>
        </footer>
      </div>

      {/* Image Modal/Lightbox */}
      {modalImage && (
        <div 
          className="image-modal" 
          onClick={() => setModalImage(null)}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on the content
          >
            <span 
              className="close-modal" 
              onClick={() => setModalImage(null)}
            >
              &times;
            </span>
            <img 
              src={modalImage} 
              alt="Enlarged question" 
              className="modal-image" 
              onClick={() => setModalImage(null)}  // Also close when clicking the image
            />
            <div className="modal-caption">Click anywhere to close</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewMyScores;
